import { VotingHistory, VotingHistoryItem } from '../types/history';

const STORAGE_KEY = 'planning_poker_history';
const MAX_HISTORY_ITEMS = 100; // 最大保存件数

/**
 * Get voting history from localStorage
 * @returns {VotingHistory} The voting history
 */
export const getVotingHistory = (): VotingHistory => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as VotingHistory;
      return {
        items: parsed.items || [],
        lastUpdated: parsed.lastUpdated || new Date().toISOString(),
      };
    }
  } catch (error) {
    console.error('Error reading voting history from localStorage:', error);
  }
  
  return {
    items: [],
    lastUpdated: new Date().toISOString(),
  };
};

/**
 * Save a new voting result to history
 * @param {VotingHistoryItem} item - The voting result to save
 */
export const saveVotingResult = (item: VotingHistoryItem): void => {
  try {
    const history = getVotingHistory();
    
    // Add the new item at the beginning
    history.items.unshift(item);
    
    // Limit the number of items
    if (history.items.length > MAX_HISTORY_ITEMS) {
      history.items = history.items.slice(0, MAX_HISTORY_ITEMS);
    }
    
    // Update timestamp
    history.lastUpdated = new Date().toISOString();
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Error saving voting result to localStorage:', error);
  }
};

/**
 * Get voting history for a specific room
 * @param {string} roomId - The room ID
 * @returns {VotingHistoryItem[]} The voting history for the room
 */
export const getRoomHistory = (roomId: string): VotingHistoryItem[] => {
  const history = getVotingHistory();
  return history.items.filter(item => item.roomId === roomId);
};

/**
 * Clear all voting history
 */
export const clearVotingHistory = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing voting history:', error);
  }
};

/**
 * Get summary statistics from voting history
 * @returns {object} Summary statistics
 */
export const getHistoryStatistics = () => {
  const history = getVotingHistory();
  const items = history.items;
  
  if (items.length === 0) {
    return {
      totalSessions: 0,
      totalRooms: 0,
      averagePoints: 0,
      mostUsedPoint: 'N/A',
    };
  }
  
  const uniqueRooms = new Set(items.map(item => item.roomId));
  const pointCounts: Record<string, number> = {};
  let totalNumericPoints = 0;
  let numericPointsCount = 0;
  
  items.forEach(item => {
    // Count point frequency
    pointCounts[item.finalPoint] = (pointCounts[item.finalPoint] || 0) + 1;
    
    // Calculate average for numeric points
    const numericPoint = parseFloat(item.finalPoint);
    if (!isNaN(numericPoint)) {
      totalNumericPoints += numericPoint;
      numericPointsCount++;
    }
  });
  
  // Find most used point
  const mostUsedPoint = Object.entries(pointCounts)
    .reduce((a, b) => pointCounts[a[0]] > pointCounts[b[0]] ? a : b)[0];
  
  return {
    totalSessions: items.length,
    totalRooms: uniqueRooms.size,
    averagePoints: numericPointsCount > 0 ? (totalNumericPoints / numericPointsCount).toFixed(1) : 'N/A',
    mostUsedPoint,
  };
};